import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AppWindow, Copy, KeyRound, Plus, ShieldCheck, TriangleAlert, X } from 'lucide-react'
import { notiApi, type ClientType, type NotiClient } from '../lib/api-noti'
import { Toggle } from '../components/Toggle'

const TYPE_LABEL: Record<ClientType, string> = {
  service: 'Service (ระบบงาน)',
  web: 'Web application',
  mobile: 'Mobile application',
  portal: 'Admin portal',
}

export default function ClientsPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [secretReveal, setSecretReveal] = useState<{ client: NotiClient; secret: string } | null>(null)

  const clients = useQuery({ queryKey: ['noti-clients'], queryFn: () => notiApi.listClients() })

  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => notiApi.patchClient(id, { enabled }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['noti-clients'] }),
  })

  return (
    <>
      <div className="page-head">
        <h2><AppWindow size={20} /> Clients</h2>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}><Plus size={16} /> สร้าง Client</button>
      </div>
      <p className="page-desc muted">
        ระบบผู้เรียกใช้ทั้งหมดของ Notification Center — แต่ละ client ถือ <b>Client Secret</b> ของตนเอง
        (เก็บเป็น hash แสดงเต็มเพียงครั้งเดียวตอนสร้างหรือ rotate) และเป็นปลายทาง in-app ของ workflow ได้
      </p>

      {clients.isError && <div className="panel error">{(clients.error as Error).message}</div>}

      <div className="panel">
        <table className="data-table">
          <thead>
            <tr><th>Client</th><th>ประเภท</th><th>Secret</th><th>สถานะ</th><th>ใช้งานล่าสุด</th><th /></tr>
          </thead>
          <tbody>
            {(clients.data?.data ?? []).map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to="/clients/$clientId" params={{ clientId: c.id }} className="row-link">
                    <b>{c.name}</b> {c.isAdmin && <ShieldCheck size={13} className="admin-mark" />}
                    <div className="muted small"><code>{c.id}</code>{c.description ? ` — ${c.description}` : ''}</div>
                  </Link>
                </td>
                <td>{TYPE_LABEL[c.type]}</td>
                <td>
                  {c.hasSecret
                    ? <code className="secret-hint">ncs_…{c.secretHint}</code>
                    : <span className="pill warn">ยังไม่ออก secret</span>}
                </td>
                <td>
                  <Toggle
                    checked={c.enabled}
                    disabled={c.isAdmin || toggle.isPending}
                    onChange={(enabled) => toggle.mutate({ id: c.id, enabled })}
                  />
                </td>
                <td className="muted small">
                  {c.secretRotatedAt ? `rotate ${new Date(c.secretRotatedAt).toLocaleDateString('th-TH')}` : '—'}
                </td>
                <td>
                  <Link to="/clients/$clientId" params={{ clientId: c.id }} className="btn-ghost">จัดการ</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <CreateClientModal
          onClose={() => setCreateOpen(false)}
          onCreated={(r) => {
            setCreateOpen(false)
            setSecretReveal(r)
            qc.invalidateQueries({ queryKey: ['noti-clients'] })
          }}
        />
      )}
      {secretReveal && <SecretRevealModal {...secretReveal} onClose={() => setSecretReveal(null)} />}
    </>
  )
}

function CreateClientModal({ onClose, onCreated }: {
  onClose: () => void
  onCreated: (r: { client: NotiClient; secret: string }) => void
}) {
  const [id, setId] = useState('')
  const [name, setName] = useState('')
  const [type, setType] = useState<ClientType>('service')
  const [description, setDescription] = useState('')

  const create = useMutation({
    mutationFn: () => notiApi.createClient({ id, name, type, description }),
    onSuccess: onCreated,
  })

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>สร้าง Client ใหม่</h3>
          <button className="icon-ghost" onClick={onClose} aria-label="ปิด"><X size={16} /></button>
        </div>
        <div className="form-grid">
          <label>Client ID
            <input value={id} onChange={(e) => setId(e.target.value)} placeholder="customer-portal" spellCheck={false} />
            <span className="muted small">a-z, 0-9, ขีดกลาง — ใช้เป็นค่า source บนทุกธุรกรรม</span>
          </label>
          <label>ชื่อระบบ
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer Portal" />
          </label>
          <label>ประเภท
            <select value={type} onChange={(e) => setType(e.target.value as ClientType)}>
              {(Object.keys(TYPE_LABEL) as ClientType[]).filter((t) => t !== 'portal').map((t) => (
                <option key={t} value={t}>{TYPE_LABEL[t]}</option>
              ))}
            </select>
          </label>
          <label>คำอธิบาย
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="(ไม่บังคับ)" />
          </label>
        </div>
        {create.isError && <div className="error">{(create.error as Error).message}</div>}
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>ยกเลิก</button>
          <button className="btn-primary" disabled={!id || !name || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'กำลังสร้าง…' : 'สร้างและออก Secret'}
          </button>
        </div>
      </div>
    </div>
  )
}

// SecretRevealModal shows the plaintext secret EXACTLY once.
export function SecretRevealModal({ client, secret, onClose }: {
  client: NotiClient; secret: string; onClose: () => void
}) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="modal-backdrop">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3><KeyRound size={17} /> Client Secret — {client.name}</h3>
        </div>
        <div className="secret-banner">
          <TriangleAlert size={16} />
          ระบบเก็บเฉพาะค่า hash — secret ฉบับเต็มแสดง<strong>ครั้งนี้ครั้งเดียว</strong> โปรดคัดลอกเก็บในที่ปลอดภัย
        </div>
        <div className="secret-box">
          <code>{secret}</code>
          <button
            className="btn-ghost"
            onClick={() => {
              navigator.clipboard.writeText(secret).then(() => setCopied(true))
            }}
          >
            <Copy size={14} /> {copied ? 'คัดลอกแล้ว' : 'คัดลอก'}
          </button>
        </div>
        <p className="muted small">
          วิธีใช้: แนบ header <code>X-Client-Id: {client.id}</code> และ <code>X-Client-Secret: …</code> ในทุกคำขอ
        </p>
        <div className="modal-actions">
          <button className="btn-primary" onClick={onClose}>รับทราบ — ปิดหน้าต่าง</button>
        </div>
      </div>
    </div>
  )
}
